import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { BikeIcon, CarIcon, FootprintsIcon, Trash2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { formatDistance } from '@modules/_shared/utils/formatting';
import type { POIItem } from '@modules/reports/shared/StepPointsOfInterest/StepPointsOfInterest';

const travelIconColor = 'var(--mui-palette-text-secondary)';
const iconWalking = <FootprintsIcon size={16} color={travelIconColor} />;
const iconCycling = <BikeIcon size={16} color={travelIconColor} />;
const iconDriving = <CarIcon size={16} color={travelIconColor} />;

export const DELETE_PROPERTY_POI = gql(/* GraphQL */ `
  mutation DeletePropertyPoi($propertyPoiId: uuid!) {
    delete_property_pois_by_pk(id: $propertyPoiId) {
      id
    }
  }
`);

type POIListItemProps = {
  poi: POIItem;
  primaryType: string;
  updateReport: () => Promise<any>;
};

const POIListItem = ({ poi, primaryType, updateReport }: POIListItemProps) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePropertyPoi] = useMutation(DELETE_PROPERTY_POI, {
    onCompleted: () => {
      updateReport();
    },
    update(cache, { data }) {
      const deletedId = data?.delete_property_pois_by_pk?.id;
      if (deletedId) {
        cache.evict({ id: `property_pois:${deletedId}` });
      }
    },
  });

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deletePropertyPoi({
        variables: {
          propertyPoiId: poi.id,
        },
      });
    } catch (error) {
      console.error('Error deleting POI:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTransportIcon = () => {
    switch (poi.primaryMode) {
      case 'walking':
        return iconWalking;
      case 'cycling':
        return iconCycling;
      case 'driving':
        return iconDriving;
      default:
        return iconDriving;
    }
  };

  return (
    <ListItem divider sx={{ p: 1, px: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="column"
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ mr: 1 }}>
              {poi.name}{' '}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                ({primaryType})
              </Typography>
            </Typography>
          </Box>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {poi.formattedAddress}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box component="span" sx={{ display: 'flex', mr: 0.5 }}>
            {getTransportIcon()}
          </Box>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {formatDistance(poi.primaryDistanceMeters)}
          </Typography>
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Trash2Icon size={18} />
            )}
          </IconButton>
        </Stack>
      </Stack>
    </ListItem>
  );
};

export default POIListItem;
